import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, FolderTree, Mail, Loader2, Plus, Boxes, PackageX, Clock, Star, FileText, CalendarCheck } from "lucide-react";
import { GlassCard, RoleBadge } from "../../components/common/ui";
import { StockBadge, formatGBP } from "../../components/common/StockBadge";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATS = [
  { key: "total_active", label: "Active products", icon: Package, color: "text-emerald-400" },
  { key: "in_stock", label: "In stock", icon: Boxes, color: "text-emerald-400" },
  { key: "out_of_stock", label: "Out of stock", icon: PackageX, color: "text-firered" },
  { key: "coming_soon", label: "Coming soon", icon: Clock, color: "text-amber" },
  { key: "featured", label: "Featured", icon: Star, color: "text-gold" },
  { key: "categories_active", label: "Active categories", icon: FolderTree, color: "text-white" },
  { key: "total_inactive", label: "Inactive products", icon: PackageX, color: "text-white/60" },
  { key: "subscribers", label: "VIP subscribers", icon: Mail, color: "text-gold" },
];

const QUICK = [
  { label: "Add Product", icon: Plus, to: "/admin/products/new", roles: ["super_admin", "manager"] },
  { label: "Add Category", icon: FolderTree, to: "/admin/categories", roles: ["super_admin", "manager"] },
  { label: "Manage Products", icon: Package, to: "/admin/products", roles: ["super_admin", "manager"] },
  { label: "Edit Homepage", icon: FileText, to: "/admin/content", roles: ["super_admin", "manager"] },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const isManager = ["super_admin", "manager"].includes(user?.role);

  useEffect(() => {
    if (!isManager) { setLoading(false); return; }
    Promise.all([
      api.get("/admin/catalogue/stats").then(({ data }) => setStats(data)).catch(() => {}),
      api.get("/admin/products", { params: { sort: "updated", limit: 5 } }).then(({ data }) => setRecent(data.items)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [isManager]);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase cf-gold-text font-bold">Admin Portal</p>
          <h1 className="mt-2 font-playfair text-3xl text-white">Dashboard</h1>
          <p className="mt-2 text-muted-foreground">Welcome, {user?.name}.</p>
        </div>
        {user?.role && <RoleBadge role={user.role} />}
      </div>

      {!isManager ? (
        <GlassCard className="p-8" hover={false}>
          <div className="flex items-center gap-3 text-white"><CalendarCheck className="h-6 w-6 text-gold" /><h2 className="font-playfair text-xl">Reservations</h2></div>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">Staff dashboard. Reservation review & approval arrives in Phase 5.</p>
        </GlassCard>
      ) : loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
      ) : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {STATS.map((s) => (
              <GlassCard key={s.key} className="p-5" data-testid={`stat-${s.key}`}>
                <div className="flex items-center justify-between">
                  <div className="grid place-items-center h-10 w-10 rounded-xl bg-white/5 border border-white/10"><s.icon className={`h-5 w-5 ${s.color}`} /></div>
                  <span className="font-playfair text-3xl text-white">{stats?.[s.key] ?? 0}</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{s.label}</p>
              </GlassCard>
            ))}
          </div>

          <div>
            <h2 className="font-playfair text-xl text-white mb-3">Quick actions</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {QUICK.filter((q) => q.roles.includes(user?.role)).map((q) => (
                <button key={q.label} data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => navigate(q.to)} className="cf-glass cf-glass-hover rounded-xl p-4 flex items-center gap-3 text-left">
                  <div className="grid place-items-center h-10 w-10 rounded-lg bg-gold/10 border border-gold/20"><q.icon className="h-5 w-5 text-gold" /></div>
                  <span className="text-white font-medium text-sm">{q.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-playfair text-xl text-white mb-3">Recently updated products</h2>
            <GlassCard className="p-0 overflow-hidden" hover={false}>
              {recent.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No products yet. <button onClick={() => navigate("/admin/products/new")} className="text-gold underline">Add your first product</button>.</div>
              ) : recent.map((p) => (
                <button key={p.product_id} onClick={() => navigate(`/admin/products/${p.product_id}/edit`)} data-testid={`recent-${p.sku}`} className="w-full flex items-center gap-3 px-5 py-3 border-b border-white/5 hover:bg-white/5 text-left">
                  {(p.images || [])[0] ? <img src={(p.images.find((m) => m.is_primary) || p.images[0]).thumb_url || p.images[0].url} alt="" className="h-9 w-9 rounded object-cover" /> : <div className="h-9 w-9 rounded bg-white/10" />}
                  <span className="flex-1 text-white text-sm">{p.name}</span>
                  <StockBadge status={p.stock_status} />
                  <span className="text-white/70 text-sm w-20 text-right">{formatGBP(p.retail_price)}</span>
                </button>
              ))}
            </GlassCard>
          </div>
        </>
      )}
    </div>
  );
}
